import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  StatCard,
  StatusBadge,
  ProgressBar,
  Avatar,
  GhostBtn,
  Ring,
  EmptyState,
} from "components/dashboard/ui";
import {
  Check,
  CheckCircle2,
  Layers,
  Boxes,
  Receipt,
  Users2,
  Activity,
  Calendar,
  Pin,
  ClipboardList,
  ArrowDownRight,
  ArrowUpRight,
  Package,
  Briefcase,
  IndianRupee,
} from "lucide-react";
import { IndividualDashboardApi } from "api/dashboard/service";
import { formatCurrencyShort } from "utils/utilityFunctions";
import { json } from "utils/http/client";
import ReminderActionDrawer, {
  getCompletedReminderActionLabel,
  getReminderActionLabel,
} from "../../Dashboard/ReminderActionDrawer";
import {
  useIndividualProjectSummary,
  useWeather,
} from "../../../utils/hooks/useSiteData";
import ProjectSummaryHeader from "./ProjectSummaryHeader";
import NewMaterialRequest from "../Materials/MaterialRequest/NewRequest";
import { IoLocationOutline } from "react-icons/io5";
import { GoClock } from "react-icons/go";

const WEATHER_API_KEY = "c093db40219c1637dbc236b56549afdd";

const ACTION_TYPE_META = {
  INVENTORY_UPDATE: { icon: Boxes },
  TASK_UPDATE: { icon: ClipboardList },
  SUBTASK_UPDATE: { icon: ClipboardList },
  WORKER_ATTENDANCE: { icon: Briefcase },
  CONTRACTOR_ATTENDANCE: { icon: Briefcase },
  VENDOR_PAYMENT: { icon: IndianRupee },
  CONTRACTOR_PAYMENT: { icon: IndianRupee },
  SITE_UPDATE: { icon: Receipt },
  WORKER_PAYMENT: { icon: IndianRupee },
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clampPercent = (value) =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const parseStoredOrganizationId = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}")?.id || null;
  } catch (_) {
    return null;
  }
};

const formatDate = (value, options = { day: "2-digit", month: "short" }) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", options);
};

const formatTime = (timeLike) => {
  if (!timeLike) return "Today";
  const [hour = "0", minute = "0"] = String(timeLike).split(":");
  const date = new Date();
  date.setHours(Number(hour), Number(minute), 0, 0);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const timeAgo = (dateLike) => {
  if (!dateLike) return "Just now";
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return String(dateLike);
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / 60000),
  );
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const statusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (
    normalized.includes("complete") ||
    normalized.includes("approved") ||
    normalized.includes("delivered")
  )
    return "good";
  if (
    normalized.includes("halt") ||
    normalized.includes("reject") ||
    normalized.includes("cancel") ||
    normalized.includes("overdue")
  )
    return "alert";
  if (
    normalized.includes("pending") ||
    normalized.includes("not started") ||
    normalized.includes("ongoing") ||
    normalized.includes("upcoming")
  )
    return "warn";
  return "neutral";
};

const taskProgress = (task) => {
  const total = safeNumber(task?.subtasks);
  const completed = safeNumber(task?.completed_subtasks);
  if (total > 0) return clampPercent((completed / total) * 100);
  return String(task?.status || "")
    .toLowerCase()
    .includes("complete")
    ? 100
    : 0;
};

const initials = (name, fallback = "?") =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || fallback;

const getTransactionType = (category) =>
  ["received", "refund"].includes(String(category || "").toLowerCase())
    ? "in"
    : "out";

const getTaskDate = (task) => {
  const start = task?.start_date ? new Date(task.start_date) : null;
  const end = task?.end_date ? new Date(task.end_date) : null;
  const validStart = start && !Number.isNaN(start.getTime()) ? start : null;
  const validEnd = end && !Number.isNaN(end.getTime()) ? end : null;
  if (validStart && validEnd)
    return new Date((validStart.getTime() + validEnd.getTime()) / 2);
  return validStart || validEnd || null;
};

const getReminderProgress = (reminder) => {
  const progress = reminder?.progress || {};
  const total = safeNumber(progress?.total);
  const completed = Math.min(
    safeNumber(progress?.completed),
    total || Number.MAX_SAFE_INTEGER,
  );
  const percent = total ? clampPercent((completed / total) * 100) : 0;
  const label =
    progress?.label ||
    (total
      ? `${completed}/${total} completed`
      : reminder?.is_completed
        ? "Completed"
        : "");

  return {
    completed,
    total,
    percent,
    label,
    isComplete: Boolean(progress?.is_complete ?? reminder?.is_completed),
  };
};

const mapReminder = (item) => {
  const Icon = ACTION_TYPE_META[item?.action_type]?.icon || ClipboardList;
  const dueState = String(item?.due_state || "").toUpperCase();
  const progress = getReminderProgress(item);
  return {
    raw: item,
    id: item?.id,
    icon: Icon,
    title: item?.title || "Scheduled action",
    site: item?.site,
    siteName: item?.site_name,
    project: item?.site_name || item?.scope || "This site",
    actionLabel: getReminderActionLabel(item?.action_type),
    completedActionLabel: getCompletedReminderActionLabel(item?.action_type),
    time: formatTime(item?.due_time),
    urgency:
      dueState === "OVERDUE"
        ? "overdue"
        : dueState === "DUE"
          ? "high"
          : undefined,
    completed: progress.isComplete,
    progress,
  };
};

const getScheduleSortRank = (item) => {
  if (item?.completed) return 4;
  if (item?.urgency === "overdue") return 0;
  if (item?.urgency === "high") return 1;
  return 2;
};

const getTimeSortValue = (item) => {
  const rawTime = item?.raw?.due_time;
  if (!rawTime) return Number.MAX_SAFE_INTEGER;
  const [hours = "0", minutes = "0"] = String(rawTime).split(":");
  return safeNumber(hours) * 60 + safeNumber(minutes);
};

const getReminderSiteCandidates = (item) =>
  [
    item?.site,
    item?.site_id,
    item?.siteId,
    item?.project_id,
    item?.metadata?.site,
    item?.metadata?.site_id,
    item?.metadata?.siteId,
    item?.payload?.site,
    item?.payload?.site_id,
    item?.site?.id,
  ].filter((value) => value !== undefined && value !== null && value !== "");

const reminderMatchesSite = (item, siteId, siteName) => {
  const idMatch = getReminderSiteCandidates(item).some(
    (value) => String(value) === String(siteId),
  );
  if (idMatch) return true;
  if (!siteName) return false;
  return (
    String(item?.site_name || item?.project || "")
      .trim()
      .toLowerCase() === String(siteName).trim().toLowerCase()
  );
};

const canViewPermission = (permissions, key) =>
  Boolean(permissions?.is_admin || permissions?.view_all || permissions?.[key]);

function ProjectIndividual() {
  const { siteId: routeSiteId } = useParams();
  const navigate = useNavigate();
  const siteId = routeSiteId || localStorage.getItem("site");
  const organizationId = useMemo(() => parseStoredOrganizationId(), []);
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [activeReminderAction, setActiveReminderAction] = useState(null);
  const [memberPermissions, setMemberPermissions] = useState(null);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const permissionsLoaded = memberPermissions !== null;

  const { data: summary } = useIndividualProjectSummary(
    organizationId,
    permissionsLoaded ? siteId : null,
  );
  const siteOverview = summary?.site_overview || {};
  const tasksOverview = summary?.tasks_overview || {};
  const assignedTasks = useMemo(
    () =>
      Array.isArray(summary?.assigned_tasks) ? summary.assigned_tasks : [],
    [summary?.assigned_tasks],
  );
  const subtaskApprovals = Array.isArray(summary?.subtasks_approvals)
    ? summary.subtasks_approvals
    : [];
  const recentPins = Array.isArray(summary?.recent_design_pins)
    ? summary.recent_design_pins
    : [];
  const workforceOverview = summary?.workforce_overview || {};
  const attendanceTrend = summary?.attendance_trend || {};
  const inventoryAlerts = summary?.inventory_alerts || {};
  const recentDeliveries = Array.isArray(summary?.recent_deliveries)
    ? summary.recent_deliveries
    : [];
  const recentTransactions = Array.isArray(
    summary?.recent_transactions?.last_five,
  )
    ? summary.recent_transactions.last_five
    : [];

  const handleDesignPinClick = (designId) => {
    if (!designId) return;
    navigate(`/dashboard/design?designId=${encodeURIComponent(designId)}`);
  };

  const {
    data: weather,
    isLoading: weatherLoading,
    isError: weatherError,
  } = useWeather(siteOverview.city || siteOverview.state, WEATHER_API_KEY);

  useEffect(() => {
    if (!siteId) {
      setMemberPermissions(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await json("/team/get-member-permission/", {
          method: "POST",
          body: { sites: siteId },
        });
        if (!cancelled) setMemberPermissions(response?.data || null);
      } catch (error) {
        console.error("Could not load project member permissions:", error);
        if (!cancelled) setMemberPermissions({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const loadReminders = useCallback(async () => {
    if (!organizationId || !siteId || !permissionsLoaded) {
      setReminders([]);
      return;
    }

    setRemindersLoading(true);
    try {
      try {
        await IndividualDashboardApi.refreshTodayReminders(organizationId);
      } catch (refreshError) {
        console.error("Could not refresh today's reminders:", refreshError);
      }

      const response =
        await IndividualDashboardApi.getTodayReminders(organizationId);
      const data = Array.isArray(response?.data) ? response.data : [];
      setReminders(
        data.filter((item) =>
          reminderMatchesSite(item, siteId, siteOverview.site_name),
        ),
      );
    } catch (error) {
      console.error("Could not load today's project reminders:", error);
      setReminders([]);
    } finally {
      setRemindersLoading(false);
    }
  }, [organizationId, permissionsLoaded, siteId, siteOverview.site_name]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const remindersDone = reminders.filter((item) => item?.is_completed).length;
  const scheduleItems = useMemo(
    () =>
      reminders.map(mapReminder).sort((left, right) => {
        const rankDelta =
          getScheduleSortRank(left) - getScheduleSortRank(right);
        if (rankDelta !== 0) return rankDelta;
        return getTimeSortValue(left) - getTimeSortValue(right);
      }),
    [reminders],
  );

  const actionFlowReminders = useMemo(
    () =>
      scheduleItems
        .filter((item) => !item.completed && item.actionLabel)
        .map((item) => item.raw),
    [scheduleItems],
  );

  const statusCounts = assignedTasks.reduce(
    (acc, task) => {
      const normalized = String(task?.status || "").toLowerCase();
      if (normalized.includes("complete")) acc.done += 1;
      else if (normalized.includes("ongoing")) acc.inProgress += 1;
      else if (normalized.includes("halt")) acc.inReview += 1;
      else acc.pending += 1;
      return acc;
    },
    { pending: 0, inProgress: 0, inReview: 0, done: 0 },
  );

  const lowStockRows = Array.isArray(inventoryAlerts.low_stocks)
    ? inventoryAlerts.low_stocks
    : [];
  const inventoryRows = lowStockRows.length
    ? lowStockRows.slice(0, 5).map((item) => {
        const currentStock = safeNumber(item.current_stock);
        const lastStock =
          item.last_stock == null ? null : safeNumber(item.last_stock);
        const isCritical =
          lastStock != null ? currentStock <= lastStock : currentStock <= 0;
        return {
          material: item.material_name || `Material #${item.material_id}`,
          subcategory: item.material_subcategory || "Misc",
          specification: item.material_specification || "",
          value: currentStock.toLocaleString("en-IN"),
          uom: item.uom || "units",
          min: lastStock == null ? "-" : lastStock.toLocaleString("en-IN"),
          pct: lastStock
            ? clampPercent((currentStock / lastStock) * 100)
            : currentStock
              ? 100
              : 0,
          tone: isCritical ? "alert" : "warn",
          label: isCritical ? "CRITICAL" : "LOW",
        };
      })
    : [
        {
          material: "No low stock alerts",
          value: "-",
          uom: "",
          min: "-",
          pct: 100,
          tone: "good",
          label: "OK",
        },
      ];

  const ganttRows = useMemo(() => {
    const today = new Date();
    return assignedTasks
      .map((task) => ({ task, date: getTaskDate(task) }))
      .sort((left, right) => {
        const leftDistance = left.date
          ? Math.abs(left.date.getTime() - today.getTime())
          : Number.MAX_SAFE_INTEGER;
        const rightDistance = right.date
          ? Math.abs(right.date.getTime() - today.getTime())
          : Number.MAX_SAFE_INTEGER;
        return leftDistance - rightDistance;
      })
      .slice(0, 6)
      .map(({ task }) => task);
  }, [assignedTasks]);

  const ganttBounds = useMemo(() => {
    const dates = ganttRows
      .flatMap((task) => [task.start_date, task.end_date])
      .map((value) => (value ? new Date(value) : null))
      .filter((date) => date && !Number.isNaN(date.getTime()));
    const today = new Date();
    dates.push(today);
    const min = new Date(Math.min(...dates.map((date) => date.getTime())));
    const max = new Date(Math.max(...dates.map((date) => date.getTime())));
    if (min.getTime() === max.getTime()) max.setDate(max.getDate() + 1);
    return { min, max, today };
  }, [ganttRows]);

  const getGanttPosition = (value, fallback) => {
    const date = value ? new Date(value) : fallback;
    const span = ganttBounds.max.getTime() - ganttBounds.min.getTime();
    if (!date || Number.isNaN(date.getTime()) || !span) return 0;
    return clampPercent(
      ((date.getTime() - ganttBounds.min.getTime()) / span) * 100,
    );
  };

  const attendanceDays = Array.isArray(attendanceTrend.last_seven_days)
    ? attendanceTrend.last_seven_days
    : [];
  const latestAttendance = attendanceDays[0]
    ? Object.values(attendanceDays[0])[0] || {}
    : {};
  const presentToday = safeNumber(
    workforceOverview.workers_present ?? latestAttendance.present,
  );
  const totalWorkers = safeNumber(workforceOverview.total_workers);
  const attendancePercent = totalWorkers
    ? clampPercent((presentToday / totalWorkers) * 100)
    : 0;
  const canViewFinance = canViewPermission(memberPermissions, "view_finance");
  const canViewMaterials = canViewPermission(
    memberPermissions,
    "view_material_management",
  );
  const canCreateMaterialRequest = Boolean(
    memberPermissions?.is_admin ||
    memberPermissions?.edit_all ||
    memberPermissions?.edit_material_management,
  );
  const canViewWorkers = canViewPermission(
    memberPermissions,
    "view_worker_management",
  );
  const subtaskCardClass = canViewMaterials
    ? "col-span-12 xl:col-span-7"
    : "col-span-12";
  const deliveryCardClass = canViewFinance
    ? "col-span-12 md:col-span-6 xl:col-span-5"
    : "col-span-12";
  const transactionCardClass = canViewMaterials
    ? "col-span-12 md:col-span-6 xl:col-span-7"
    : "col-span-12";
  const workerCardClass = "col-span-12 md:col-span-6";
  const designPinsCardClass = canViewWorkers
    ? "col-span-12 md:col-span-6"
    : "col-span-12";

  return (
    <div className="min-h-screen px-8 py-4">
      <div className="grid grid-cols-12 gap-6">
        <ProjectSummaryHeader
          summary={summary}
          weather={weather}
          weatherLoading={weatherLoading}
          weatherError={weatherError}
          source="individual"
        />

        {/* TASK STATUS */}
        <Card
          className="col-span-12"
          title="Task Status"
          icon={<Activity className="size-3.5" />}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Total Assigned"
              value={tasksOverview.total_tasks || 0}
              tone="neutral"
            />
            <StatCard
              label="Ongoing Tasks"
              value={safeNumber(tasksOverview.ongoing_tasks)}
              tone="neutral"
            />
            <StatCard
              label="Delayed"
              value={safeNumber(tasksOverview.delayed_tasks)}
              tone="warn"
            />
            <StatCard
              label="Done"
              value={safeNumber(tasksOverview.completed_tasks)}
              tone="good"
            />
          </div>
        </Card>

        {/* TODAY'S TODO */}
        <Card
          className="col-span-12"
          title="Today's Schedule"
          icon={<CheckCircle2 className="size-3.5" />}
          action={
            <StatusBadge tone="info">
              {remindersLoading
                ? "Refreshing"
                : `${remindersDone}/${reminders.length} done`}
            </StatusBadge>
          }
        >
          <ul className="space-y-2">
            {scheduleItems.length ? (
              scheduleItems.map((item) => {
                const tone =
                  item.urgency === "overdue"
                    ? "alert"
                    : item.urgency === "high"
                      ? "warn"
                      : undefined;
                const Icon = item.icon;
                return (
                  <li
                    key={item.id}
                    onClick={() => {
                      if (item.actionLabel) setActiveReminderAction(item.raw);
                    }}
                    onKeyDown={(event) => {
                      if (
                        !item.actionLabel ||
                        !["Enter", " "].includes(event.key)
                      )
                        return;
                      event.preventDefault();
                      setActiveReminderAction(item.raw);
                    }}
                    role={item.actionLabel ? "button" : undefined}
                    tabIndex={item.actionLabel ? 0 : undefined}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-border hover:border-border-strong transition-colors ${item.actionLabel ? "cursor-pointer" : ""} ${tone === "alert" ? "border-l-4 border-l-red-500 bg-red-50/40" : tone === "warn" ? "border-l-4 border-l-amber-400 bg-amber-50/40" : ""}`}
                  >
                    <div
                      className={`size-5 rounded-md border-2 shrink-0 ${item.completed ? "border-emerald-500 bg-emerald-500" : "border-border-strong hover:border-primary"}`}
                    >
                      {item.completed && (
                        <Check className="size-4 text-white" />
                      )}
                    </div>
                    <div className="size-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Icon className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium leading-tight">
                        {item.title}
                      </div>
                      <div className="flex flex-row items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <IoLocationOutline size={12} />
                        <span>{item.project}</span>
                        <GoClock className="ml-2" />
                        <span>{item.time}</span>
                      </div>
                      {item.progress?.label && (
                        <div className="mt-1 w-[85%] flex items-center gap-2">
                          <div className="h-1.5 flex-1 w-[70%] overflow-hidden rounded-full bg-muted">
                            <div
                              className={`h-full rounded-full ${item.completed ? "bg-emerald-500" : "bg-[#3B0069]"}`}
                              style={{ width: `${item.progress.percent}%` }}
                            />
                          </div>
                          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-muted-foreground">
                            {item.progress.label}
                          </span>
                        </div>
                      )}
                    </div>
                    {item.completed && (
                      <StatusBadge tone="good">DONE</StatusBadge>
                    )}
                    {!item.completed && item.urgency === "high" && (
                      <StatusBadge tone="alert">HIGH</StatusBadge>
                    )}
                    {!item.completed && item.urgency === "overdue" && (
                      <StatusBadge tone="warn">OVERDUE</StatusBadge>
                    )}
                    {item.actionLabel && !item.completed && (
                      <span className="shrink-0 rounded-lg bg-[#3B0069] px-3 py-1.5 text-xs font-semibold capitalize text-white transition hover:bg-[#2d0050]">
                        {item.actionLabel}
                      </span>
                    )}
                    {item.completed && item.completedActionLabel && (
                      <span className="shrink-0 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold capitalize text-white">
                        {item.completedActionLabel}
                      </span>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="p-4 text-center text-sm text-muted-foreground">
                No schedule items for this site today.
              </li>
            )}
          </ul>
        </Card>

        {/* SUBTASK UPDATES */}
        <Card
          className={subtaskCardClass}
          title="Subtask Updates · Approvals"
          icon={<Layers className="size-3.5" />}
          action={
            <StatusBadge tone="info">
              {subtaskApprovals.length} updates
            </StatusBadge>
          }
        >
          <div className="space-y-2">
            {(subtaskApprovals.length
              ? subtaskApprovals.slice(0, 4)
              : [
                  {
                    subtask_name: "No subtask approvals",
                    task_name: "Updates clear",
                    approval_status: "approved",
                    created_by: "System",
                  },
                ]
            ).map((approval, index) => {
              const tone = statusTone(approval.approval_status || "pending");
              return (
                <div
                  key={`${approval.subtask_name}-${index}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border border-border ${tone === "good" ? "bg-emerald-50/50" : tone === "warn" ? "bg-amber-50/40" : tone === "alert" ? "bg-red-50/40" : ""}`}
                >
                  <Avatar
                    initials={initials(approval.created_by, "AP")}
                    tone={tone === "alert" ? "alert" : "primary"}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {approval.subtask_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {approval.task_name} · {approval.created_by || "Team"} ·{" "}
                      {timeAgo(approval.created_on)} ago
                    </div>
                    {/* comment  approval.comment */}
                    <div className="text-sm text-muted-foreground">
                      {approval.comment}
                    </div>
                    {/* <div className="mt-1.5"><ProgressBar value={pct} tone={tone === "good" ? "good" : tone === "alert" ? "alert" : "primary"} /></div> */}
                  </div>
                  {tone === "good" && (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                      <CheckCircle2 className="size-4" />
                      Approved
                    </span>
                  )}
                  {tone !== "good" && (
                    <StatusBadge tone={tone}>
                      {approval.approval_status || "Pending"}
                    </StatusBadge>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* MATERIAL INVENTORY */}
        {canViewMaterials && (
          <Card
            className="col-span-12 xl:col-span-5"
            title="Material Inventory"
            icon={<Boxes className="size-3.5" />}
            action={
              canCreateMaterialRequest ? (
                <button
                  type="button"
                  onClick={() => setShowNewRequestForm(true)}
                  className="inline-flex h-7 items-center rounded-lg bg-[#3B0069] px-3 text-xs font-semibold text-white transition-colors hover:bg-[#2d0050]"
                >
                  Raise MR
                </button>
              ) : null
            }
          >
            <div className="space-y-2">
              {inventoryRows.map((item) => {
                const dot =
                  item.tone === "alert"
                    ? "bg-red-500"
                    : item.tone === "warn"
                      ? "bg-amber-500"
                      : "bg-emerald-500";
                const valueColor =
                  item.tone === "alert"
                    ? "text-red-600"
                    : item.tone === "warn"
                      ? "text-amber-600"
                      : "text-emerald-600";
                return (
                  <div
                    key={item.material}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-border ${item.tone === "alert" ? "bg-red-50/50" : ""}`}
                  >
                    <span className={`size-2.5 rounded-full ${dot} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {item.material}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-md bg-muted/40 truncate">{item.subcategory}</span>
                        {item.specification ? (
                          <span className="text-xs text-muted-foreground px-2 py-0.5 rounded-md bg-muted/40 truncate">{item.specification}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-col items-end min-w-[90px]">
                      <div className={`text-sm font-semibold ${valueColor}`}>
                        {item.value} {item.uom}
                      </div>
                      {/* <div className="text-xs text-muted-foreground font-mono">
                        Min: {item.min}
                      </div> */}
                    </div>
                    {/* removed StatusBadge ("Critical") per UI cleanup */}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {showNewRequestForm && (
          <NewMaterialRequest onClose={() => setShowNewRequestForm(false)} />
        )}

        {/* GANTT */}
        <Card
          className="col-span-12"
          title="Task Gantt · Overview"
          icon={<Calendar className="size-3.5" />}
          action={
            <span className="text-xs text-muted-foreground">
              Closest 6 tasks
            </span>
          }
        >
          <div className="space-y-2.5">
            {ganttRows.length ? (
              ganttRows.map((task) => {
                const start = getGanttPosition(
                  task.start_date,
                  getTaskDate(task) || ganttBounds.min,
                );
                const end = getGanttPosition(
                  task.end_date,
                  getTaskDate(task) || ganttBounds.max,
                );
                const left = Math.min(start, end);
                const width = Math.max(3, Math.abs(end - start));
                const pct = taskProgress(task);
                const tone = statusTone(task.status);
                const color =
                  tone === "good"
                    ? "bg-emerald-500"
                    : tone === "alert"
                      ? "bg-red-500"
                      : tone === "warn"
                        ? "bg-amber-500"
                        : "bg-blue-500";
                return (
                  <div
                    key={`${task.task_group_name}-${task.task_name}-${task.start_date}`}
                    className="flex items-center gap-3"
                  >
                    <div className="w-[150px] text-xs font-medium truncate">
                      {task.task_name}
                    </div>
                    <div className="relative flex-1 h-6 rounded-md bg-muted">
                      <div className="absolute inset-0 flex pointer-events-none">
                        {Array.from({ length: 7 }).map((_, i) => (
                          <div
                            key={i}
                            className="flex-1 border-r border-dashed border-border/60 last:border-0"
                          />
                        ))}
                      </div>
                      <div
                        className={`absolute top-0 bottom-0 rounded-md bg-sm-accent border-2 ${color.replace("bg-", "border-")} opacity-20`}
                        style={{ left: `${left}%`, width: `${width}%` }}
                      />
                      <div
                        className={`absolute top-0 bottom-0 bg-sm-accent rounded-md ${color}`}
                        style={{
                          left: `${left}%`,
                          width: `${(width * pct) / 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                      {pct}%
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No assigned tasks to display.
              </div>
            )}
            <div className="relative h-px bg-border mt-2 ml-[150px] mr-12">
              <div
                className="absolute -top-1 bottom-0 w-px bg-red-500 border-l border-dashed border-red-500"
                style={{
                  left: `${getGanttPosition(null, ganttBounds.today)}%`,
                  height: "12px",
                }}
              />
              <div
                className="absolute -top-7 text-[10px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded"
                style={{
                  left: `${getGanttPosition(null, ganttBounds.today)}%`,
                  transform: "translateX(-50%)",
                }}
              >
                TODAY
              </div>
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground font-medium uppercase pt-2 ml-[150px] mr-12">
              <span>{formatDate(ganttBounds.min)}</span>
              <span>{formatDate(ganttBounds.max)}</span>
            </div>
          </div>
        </Card>

        {/* DELIVERIES */}
        {canViewMaterials && (
          <Card
            className={deliveryCardClass}
            title="Recent Deliveries"
            icon={<Package className="size-3.5" />}
          >
            <div className="space-y-1">
              {(recentDeliveries.length
                ? recentDeliveries.slice(0, 5)
                : [
                    {
                      po_number: "No deliveries",
                      vendor_name: "Delivery history clear",
                      delivery_status: "Delivered",
                      materials: [],
                    },
                  ]
              ).map((delivery, index) => {
                const materialNames = (delivery.materials || [])
                  .map((material) => material.material_name)
                  .filter(Boolean);
                const label = materialNames.length
                  ? materialNames.join(", ")
                  : delivery.po_number;
                const tone = statusTone(delivery.delivery_status);
                return (
                  <div
                    key={`${delivery.po_number}-${index}`}
                    className="flex items-center gap-3 py-3 px-2 -mx-2 border-b border-border/50 last:border-0"
                  >
                    <Avatar
                      initials={initials(delivery.vendor_name, "DL")}
                      tone={tone === "good" ? "good" : "warn"}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {label}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {delivery.vendor_name || "-"} ·{" "}
                        {formatDate(delivery.date || delivery.created_on)}
                      </div>
                    </div>
                    <StatusBadge tone={tone}>
                      {delivery.delivery_status || "Recorded"}
                    </StatusBadge>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* TRANSACTIONS */}
        {canViewFinance && (
          <Card
            className={transactionCardClass}
            title="Recent Transactions"
            icon={<Receipt className="size-3.5" />}
          >
            <div className="space-y-1">
              {(recentTransactions.length
                ? recentTransactions
                : [
                    {
                      description: "No recent transactions",
                      amount: 0,
                      category: "received",
                      transaction_date: null,
                    },
                  ]
              ).map((transaction, index) => {
                const type = getTransactionType(transaction.category);
                const amount = formatCurrencyShort(
                  safeNumber(transaction.amount),
                );
                return (
                  <div
                    key={`${transaction.transaction_id || transaction.description}-${index}`}
                    className="flex items-center gap-3 py-3 px-2 -mx-2 border-b border-border/50 last:border-0"
                  >
                    <div
                      className={`size-9 rounded-full flex items-center justify-center ${type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}
                    >
                      {type === "in" ? (
                        <ArrowUpRight className="size-4" />
                      ) : (
                        <ArrowDownRight className="size-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {transaction.description}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {transaction.party || transaction.payment_method || "-"}{" "}
                        · {formatDate(transaction.transaction_date)}
                      </div>
                    </div>
                    <div
                      className={`font-mono font-bold text-sm ${type === "in" ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {type === "in" ? "+" : "-"}
                      {amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* WORKER ATTENDANCE */}
        {canViewWorkers && (
          <Card
            className={workerCardClass}
            title="Worker Attendance"
            icon={<Users2 className="size-3.5" />}
            action={
              <span className="text-[11px] uppercase font-semibold text-muted-foreground tracking-wide">
                Today
              </span>
            }
          >
            <div className="flex items-center gap-5 mb-4">
              <Ring
                value={attendancePercent}
                size={84}
                stroke={9}
                color="#10b981"
                label={<span className="text-base">{attendancePercent}%</span>}
              />
              <div>
                <div className="text-3xl font-bold tracking-tight">
                  {presentToday}
                  <span className="text-base text-muted-foreground font-medium">
                    /{totalWorkers}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  present today
                </div>
                <StatusBadge
                  tone={
                    attendancePercent >= 80
                      ? "good"
                      : attendancePercent >= 50
                        ? "warn"
                        : "alert"
                  }
                  className="mt-1"
                >
                  {safeNumber(workforceOverview.overtime)}h overtime
                </StatusBadge>
              </div>
            </div>
            <div className="space-y-2.5">
              {[
                {
                  l: "Workers",
                  v: `${presentToday}/${totalWorkers}`,
                  pct: attendancePercent,
                  tone: attendancePercent >= 80 ? "good" : "warn",
                },
                {
                  l: "Contractors",
                  v: safeNumber(workforceOverview.contractor_attendance),
                  pct: totalWorkers
                    ? clampPercent(
                        (safeNumber(workforceOverview.contractor_attendance) /
                          totalWorkers) *
                          100,
                      )
                    : 0,
                  tone: "info",
                },
                {
                  l: "Active WOs",
                  v: safeNumber(workforceOverview.active_workorders),
                  pct: clampPercent(
                    safeNumber(workforceOverview.active_workorders) * 10,
                  ),
                  tone: "primary",
                },
              ].map((item) => (
                <div key={item.l}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{item.l}</span>
                    <span className="text-muted-foreground font-mono">
                      {item.v}
                    </span>
                  </div>
                  <ProgressBar
                    value={item.pct}
                    tone={item.tone === "info" ? "primary" : item.tone}
                  />
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* DESIGN PINS */}
        <Card
          className={designPinsCardClass}
          title="Design Pins"
          icon={<Pin className="size-3.5" />}
          action={
            <StatusBadge tone="info">{recentPins.length} Open</StatusBadge>
          }
        >
          <div className="space-y-1">
            {(recentPins.length ? recentPins : []).map((pin, index) => (
              <div
                key={`${pin.design_id || pin.design_name}-${index}`}
                className={`flex items-center gap-3 py-3 px-2 -mx-2 border-b border-border/50 last:border-0 ${pin.design_id ? "cursor-pointer rounded-lg transition-colors hover:bg-muted/40" : ""}`}
                onClick={() => handleDesignPinClick(pin.design_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleDesignPinClick(pin.design_id);
                  }
                }}
                role={pin.design_id ? "button" : undefined}
                tabIndex={pin.design_id ? 0 : undefined}
              >
                <Avatar
                  initials={initials(pin.created_by, "DS")}
                  tone="accent"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {pin.comment || pin.design_name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    by {pin.created_by || "Team"} · {pin.status || "pending"}
                  </div>
                </div>
                <GhostBtn>Review →</GhostBtn>
              </div>
            ))}
            {!recentPins.length && (
              <EmptyState
                icon={<Pin className="size-5" />}
                label="No recent design pins"
              />
            )}
          </div>
        </Card>
      </div>

      <ReminderActionDrawer
        reminder={activeReminderAction}
        reminders={actionFlowReminders}
        organizationId={organizationId}
        onClose={() => setActiveReminderAction(null)}
        onSaved={(event) => {
          if (event?.done !== false) {
            setActiveReminderAction(null);
          } else if (event?.nextReminder) {
            setActiveReminderAction(event.nextReminder);
          }
          loadReminders();
        }}
      />
    </div>
  );
}

export default ProjectIndividual;
